import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Token management
const TOKEN_KEY = 'citedai_session_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Still send cookies as fallback
  headers: { 'Content-Type': 'application/json' },
});

// Add Authorization header to all requests if token exists
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const auth = {
  processSession: (sessionId) => api.post('/api/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/api/auth/me'),
  logout: () => {
    clearToken();
    return api.post('/api/auth/logout');
  },
};

// Brands
export const brands = {
  list: () => api.get('/api/brands').then(r => r.data),
  get: (brandId) => api.get(`/api/brands/${brandId}`).then(r => r.data),
  create: (data) => api.post('/api/brands', data).then(r => r.data),
  update: (brandId, data) => api.patch(`/api/brands/${brandId}`, data).then(r => r.data),
  delete: (brandId) => api.delete(`/api/brands/${brandId}`).then(r => r.data),
  getStats: (brandId) => api.get(`/api/brands/${brandId}/stats`).then(r => r.data),
};

// Queries
export const queries = {
  list: (brandId, params) => {
    const searchParams = new URLSearchParams();
    if (params?.priority) searchParams.set('priority', params.priority);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    const qs = searchParams.toString() ? `?${searchParams}` : '';
    return api.get(`/api/brands/${brandId}/queries${qs}`).then(r => r.data);
  },
  create: (brandId, queriesArr) =>
    api.post(`/api/brands/${brandId}/queries`, { queries: queriesArr }).then(r => r.data),
};

// Actions
export const actions = {
  list: (brandId, params) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.type) searchParams.set('type', params.type);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const qs = searchParams.toString() ? `?${searchParams}` : '';
    return api.get(`/api/brands/${brandId}/actions${qs}`).then(r => r.data);
  },
  create: (brandId, data) => api.post(`/api/brands/${brandId}/actions`, data).then(r => r.data),
  update: (brandId, actionId, data) => api.patch(`/api/brands/${brandId}/actions/${actionId}`, data).then(r => r.data),
};

// Reddit
export const reddit = {
  list: (brandId, params) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const qs = searchParams.toString() ? `?${searchParams}` : '';
    return api.get(`/api/brands/${brandId}/reddit${qs}`).then(r => r.data);
  },
  discover: (brandId) => api.post(`/api/brands/${brandId}/reddit`).then(r => r.data),
  update: (brandId, data) => api.patch(`/api/brands/${brandId}/reddit`, data).then(r => r.data),
};

// Content
export const content = {
  list: (brandId) => api.get(`/api/brands/${brandId}/content`).then(r => r.data),
  add: (brandId, data) => api.post(`/api/brands/${brandId}/content`, data).then(r => r.data),
};

// Health
export const health = {
  get: (brandId) => api.get(`/api/brands/${brandId}/health`).then(r => r.data),
};

// Competitors
export const competitors = {
  list: (brandId) => api.get(`/api/brands/${brandId}/competitors`).then(r => r.data),
  add: (brandId, data) => api.post(`/api/brands/${brandId}/competitors`, data).then(r => r.data),
  delete: (brandId, compId) => api.delete(`/api/brands/${brandId}/competitors/${compId}`).then(r => r.data),
};

// API Keys
export const apiKeys = {
  list: () => api.get('/api/api-keys').then(r => r.data),
  create: (data) => api.post('/api/api-keys', data).then(r => r.data),
  revoke: (keyId) => api.delete(`/api/api-keys/${keyId}`).then(r => r.data),
};

// History
export const history = {
  get: (params) => {
    const searchParams = new URLSearchParams();
    if (params?.brand_id) searchParams.set('brand_id', params.brand_id);
    if (params?.days) searchParams.set('days', params.days.toString());
    const qs = searchParams.toString() ? `?${searchParams}` : '';
    return api.get(`/api/history${qs}`).then(r => r.data);
  },
};

// Benchmarks
export const benchmarks = {
  get: (category) => {
    const qs = category ? `?category=${category}` : '';
    return api.get(`/api/benchmarks${qs}`).then(r => r.data);
  },
};

// Sources
export const sources = {
  get: (brandId) => {
    const qs = brandId ? `?brand_id=${brandId}` : '';
    return api.get(`/api/sources${qs}`).then(r => r.data);
  },
};

// Stats
export const stats = {
  quick: () => api.get('/api/stats/quick').then(r => r.data),
};

// Check Query (public)
export const checkQuery = (data) =>
  api.post('/api/check-query', data).then(r => r.data);

export default {
  auth, brands, queries, actions, reddit, content,
  health, competitors, apiKeys, history, benchmarks,
  sources, stats, checkQuery, getToken, setToken, clearToken,
};
